<template>
    <status-badge v-if="task.severity === 'Error'"
                  :icon="task.isCompleted ? 'b-check-circle-fill' : 'b-x-circle-fill'"
                  :variant="task.isCompleted ? 'success' : 'danger'"
                  :title="task.isCompleted ? 'Erledigt' : 'Fehler'"
                  :position="position"
                  :class="{ 'task-completed': task.isCompleted, 'task-subtle': subtle }">
        <div class="task-content">
            <span class="task-description" :class="{ 'text-muted': task.isCompleted }">
                <span v-if="additionalText" class="additional-text">{{ additionalText }}:</span>
                {{ task.description['de-CH'] }}
            </span>
            <b-button v-if="!task.isCompleted && isEditable" size="sm" variant="success" @click="onTaskSolved" class="task-solve-btn" :disabled="isResolving">
                <b-spinner v-if="isResolving" small></b-spinner>
                <b-icon v-else icon="b-check-circle-fill" />&nbsp;{{ isResolving ? 'Wird verarbeitet...' : 'Als erledigt markieren' }}
            </b-button>
            <span v-else-if="task.isCompleted" class="task-resolved-indicator">
                <b-icon icon="b-check-circle-fill" />&nbsp;Erledigt durch <b-badge variant="success">{{ task.completedBy }}</b-badge>
            </span>
            <span v-else class="task-resolved-indicator">
                <b-icon icon="b-exclamation-circle-fill" />&nbsp;Nicht erledigt
            </span>
        </div>
    </status-badge>
    <status-badge v-else-if="task.severity === 'Warning'"
                  :icon="task.isCompleted ? 'b-check-circle-fill' : 'b-exclamation-circle-fill'"
                  :variant="task.isCompleted ? 'success' : 'warning'"
                  :title="task.isCompleted ? 'Erledigt' : 'Warnung'"
                  :position="position"
                  :class="{ 'task-completed': task.isCompleted, 'task-subtle': subtle }">
        <div class="task-content">
            <span class="task-description" :class="{ 'text-muted': task.isCompleted }">
                <span v-if="additionalText" class="additional-text">{{ additionalText }}:</span>
                {{ task.description['de-CH'] }}
            </span>
            <b-button v-if="!task.isCompleted && isEditable" size="sm" variant="success" @click="onTaskSolved" class="task-solve-btn" :disabled="isResolving">
                <b-spinner v-if="isResolving" small></b-spinner>
                <b-icon v-else icon="b-check-circle-fill" />&nbsp;{{ isResolving ? 'Wird verarbeitet...' : 'Als erledigt markieren' }}
            </b-button>
            <span v-else-if="task.isCompleted" class="task-resolved-indicator">
                <b-icon icon="b-check-circle-fill" />&nbsp;Erledigt durch <b-badge variant="success">{{ task.completedBy }}</b-badge>
            </span>
            <span v-else class="task-resolved-indicator">
                <b-icon icon="b-exclamation-circle-fill" />&nbsp;Nicht erledigt
            </span>
        </div>
    </status-badge>
    <status-badge v-else
                  :icon="task.isCompleted ? 'b-check-circle-fill' : 'b-check-circle-fill'"
                  :variant="task.isCompleted ? 'success' : 'success'"
                  :title="task.isCompleted ? 'Erledigt' : 'Erfolgreich'"
                  :position="position"
                  :class="{ 'task-completed': task.isCompleted, 'task-subtle': subtle }">
        <div class="task-content">
            <span class="task-description" :class="{ 'text-muted': task.isCompleted }">
                <span v-if="additionalText" class="additional-text">{{ additionalText }}:</span>
                {{ task.description['de-CH'] }}
            </span>
            <b-button v-if="!task.isCompleted && isEditable" size="sm" variant="success" @click="onTaskSolved" class="task-solve-btn" :disabled="isResolving">
                <b-spinner v-if="isResolving" small></b-spinner>
                <b-icon v-else icon="b-check-circle-fill" />&nbsp;{{ isResolving ? 'Wird verarbeitet...' : 'Als erledigt markieren' }}
            </b-button>
            <span v-else-if="task.isCompleted" class="task-resolved-indicator">
                <b-icon icon="b-check-circle-fill" />&nbsp;Erledigt durch <b-badge variant="success">{{ task.completedBy }}</b-badge>
            </span>
            <span v-else class="task-resolved-indicator">
                <b-icon icon="b-exclamation-circle-fill" />&nbsp;Nicht erledigt
            </span>
        </div>
    </status-badge>
</template>

<script>
import StatusBadge from '@/components/order/orderly/StatusBadge';

export default {
    name: 'TaskItem',
    components: {
        StatusBadge
    },
    props: {
        task: {
            type: Object,
            required: true
        },
        position: {
            type: String,
            default: 'single',
            validator: value => ['first', 'middle', 'last', 'single'].includes(value)
        },
        additionalText: {
            type: String,
            default: ''
        },
        subtle: {
            type: Boolean,
            default: false
        },
        order: {
            type: Object,
            required: false
        }
    },
    data() {
        return {
            isResolving: false,
            editableOrderStates: ['Open', 'Executed', 'Ordered', 'Processing']
        }
    },
    computed: {
        isEditable() {
            return this.order && this.editableOrderStates.includes(this.order.orderState)
        }
    },
    methods: {
        onTaskSolved() {
            this.isResolving = true
            this.$store.dispatch('orderlytasks/resolveTask', { taskId: this.task.id })
                .then(() => {
                    this.$emit('task-resolved', this.task.id)
                    this.isResolving = false
                })
        }
    }
}
</script>

<style lang="scss" scoped>
.task-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
}

.task-description {
    flex: 1;
    margin-right: 1rem;

    &.text-muted {
        text-decoration: line-through;
        opacity: 0.7;
    }

    .additional-text {
        font-weight: 600;
        margin-right: 0.25rem;
    }
}

.task-solve-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    white-space: normal;
    text-align: center;
    line-height: 1.2;
    flex-shrink: 0;
}

.task-resolved-indicator {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    color: #28a745;
    font-weight: 600;
    flex-shrink: 0;

    .b-icon {
        font-size: 1rem;
    }
}

// Additional styling for completed tasks (now using success variant)
:deep(.task-completed) {
    .status-badge-inner {
        opacity: 0.8;
    }
}

// Subtle styling for tasks within basket items
:deep(.task-subtle) {
    .status-badge-inner {
        font-size: 0.875rem;
        padding: 0.25rem 0.5rem;
        border-radius: 0.25rem;
    }

    .task-content {
        .task-description {
            font-size: 0.875rem;
        }

        .task-solve-btn {
            font-size: 0.75rem;
            padding: 0.25rem 0.5rem;
        }
    }
}
</style>
